// A thin wrapper around Google Analytics event tracking
import Merge from 'deepmerge';
import get from 'lodash/get';

import Cookie from '@wetransfer/concorde-cookie';

import { TrackJS } from 'trackjs';

import Config from '@app/config';
import scriptLoader from '@helper/script-loader';

import AmazonPixel from '@constant/tracking/amazon';
import AppNexusPixel from '@constant/tracking/app-nexus';
import FacebookScript from '@constant/tracking/facebook';
import LinkedinPixel from '@constant/tracking/linkedin';
import RedditScript from '@constant/tracking/reddit';

import LaunchDarkly from '@module/launchdarkly';
import { FF_PIXEL_TRACKING } from '@constant/experiments';

const referralCookie = 'wt_trk';

let firstTime = true;
let timers = {};
let settings = {
  domain: location.hostname,
  name: 'analytics',
  value: '1',
  lazyload: [],
  useDataLayer: true,
};
const dataLayerQueue = [];
export default {
  /**
   * Configure Analytics module options
   * @param  {Object} opts Module options
   */
  configure(opts = {}) {
    settings = Merge(settings, opts);
  },

  /**
   * Enable analytics tracking. We will set a cookie!
   */
  enable() {
    // Set cookie if Analytics was not enabled yet
    // This allow us to remember the user
    if (!this.isEnabled()) {
      Cookie.set(settings.name, settings.value, {
        days: 365,
        domain: settings.domain,
      });

      dataLayerQueue.forEach(this.pushToDataLayer.bind(this));
      dataLayerQueue.length = 0;
    }

    this.loadAnalyticSDK();

    // Process lazyloadable scripts
    for (const loader of settings.lazyload) {
      typeof loader === 'function' && loader();
    }

    // Remove them (just in case .enable is called again)
    settings.lazyload = [];
  },

  /**
   * Make sure the terms/conditions cookie is set.
   * @return {Boolean} True if session has been accepted or T&C have been accepted
   */
  isEnabled() {
    return this.hasAccepted();
  },

  /**
   * Check if terms/conditions cookie is set.
   * @return {Boolean} True if T&C cookie hase been accepted. False in other case.
   */
  hasAccepted() {
    return String(Cookie.get(settings.name)) === settings.value;
  },

  /**
   * check if Google Analytics is available
   * @param  {String}  type Name of the global variable to check
   * @return {Boolean}      True if GA is available. False in other case.
   */
  isAvailable(type = 'dataLayer') {
    if (typeof window[type] === 'undefined') {
      return false;
    }

    const methods = [];
    if (type === 'dataLayer') {
      methods.push('push');
    }

    // Check if certain methods are available
    for (const name of methods) {
      if (typeof window[type][name] === 'undefined') {
        return false;
      }
    }

    return true;
  },

  getReferralKey() {
    return Cookie.get(referralCookie);
  },

  setReferralKey(params) {
    // ==============================================================
    // Acquisition Tracker - Referral Key

    // User Growth & Analytics uses Referral key to segment traffic from multiple acquisition channels on WT site.
    // (e.g https://wetransfer.com/plus?trk=WT201610_HalfPanel_GotPlusClick)

    // WT's acquisition channels include but not limit to:
    // 1. Wallpaper CTA clicks
    // 2. Splashpage CTA clicks (param might change based on experiments)
    // 3. Silent redirect to sign-in page on 3rd visit to home page
    // 4. Plus page CTA clicks

    // If a user clicks on one of the acquisition CTA then a referral key is attached to that user for 1 hour. If user signup during that period we will store the referral key with the signup records. An ETL job will store it into Redshift which enables us to create acquisition reports like https://metabase.wetransfer.net/question/1914

    // Please assign @azclub for review if you need to change this.
    // ==============================================================
    if (Cookie.get(referralCookie)) {
      return;
    }

    // Keep `trk` param persistent in cookie for 1 hour.
    const referral_key = new URLSearchParams(params).get('trk');
    if (!referral_key) {
      return;
    }

    const expires = new Date(Date.now() + 60 * 60 * 1000);
    Cookie.set(referralCookie, referral_key, {
      expires,
    });
  },

  /**
   * Register a page view event. If it's first page view,
   * GA will send the event on page load
   * @param {String} path Navigation path
   */
  pageView(path) {
    this.recordSnowplowPageView(path);
    this.recordTrackersPageView(path);

    if (firstTime) {
      firstTime = false;
      return;
    }

    this.pushToDataLayer({
      event: 'pageview',
      virtualPageTitle: window.document.title,
      virtualPagePath: path,
    });
  },

  /**
   * Push an event to dataLayer
   * @param  {Object} data Data to send
   */
  pushDataLayerEvent(data = {}) {
    this.pushToDataLayer(
      Merge(
        {
          action: undefined,
          label: undefined,
          event: 'event',
          category: undefined,
          virtualPageLocation: undefined,
          virtualPageTitle: undefined,
          virtualPagePath: undefined,
          value: undefined,
          variant: undefined,
          transfer: undefined,
        },
        data
      )
    );
  },

  /**
   * Push some data to dataLayer
   * @param  {Object} data Data to send
   */
  pushToDataLayer(data = {}) {
    if (!this.hasAccepted()) {
      dataLayerQueue.push(data);
      return null;
    }

    if (!this.isAvailable('dataLayer') || !settings.useDataLayer) {
      return null;
    }

    window.dataLayer.push(data);
  },

  /**
   * Start timing an event
   * @param  {String} name Name of the timer to start
   */
  startTimer(name) {
    timers[name] = Date.now();
  },

  /**
   * Stop a timming event
   * @param  {String} name Name of the timer to stop. Must match a start timer name
   * @return {int}         Duration of the timer, in miliseconds
   */
  stopTimer(name) {
    const duration = Date.now() - timers[name];
    delete timers[name];
    return duration;
  },

  /**
   * Get GA clientId property value
   * @return {String} GA clientId value
   */
  get clientId() {
    try {
      const tracker = ga.getAll()[0];
      return tracker.get('clientId');
    } catch (e) {
      return null;
    }
  },

  /**
   * Reset module internal values
   */
  reset() {
    firstTime = true;
    settings = {
      domain: location.hostname,
      name: 'analytics',
      value: '1',
      lazyload: [],
      useDataLayer: true,
    };
    timers = {};
  },

  /**
   * Record a user's first visit to the website (timestamp)
   */
  recordFirstVisitTimestamp(cookieName = 'wt_first_visit') {
    if (!Cookie.get(cookieName)) {
      Cookie.set(cookieName, Date.now(), {
        days: 365,
        domain: settings.domain,
      });
    }
  },

  trackUserConsent() {
    if (!window.__snowplow__) {
      return;
    }

    window.__snowplow__(
      'trackConsentGranted:co',
      '1', // Consent document id
      settings.value, // Consent document version
      settings.name, // Consent document name
      null, // Consent document description
      null, // Expiry of consent
      null, // Custom context for the event
      new Date().getTime() // When the acceptence occurred
    );
  },

  trackStructEvent({
    category = '',
    action = '',
    label = '',
    property = '',
  } = {}) {
    // https://github.com/snowplow/snowplow/wiki/2-Specific-event-tracking-with-the-Javascript-tracker#371-trackstructevent
    if (!window.__snowplow__) {
      return;
    }
    if (!category) {
      throw new Error('Category is required');
    }

    if (!action) {
      throw new Error('Action is required');
    }

    window.__snowplow__(
      'trackStructEvent:co',
      category,
      action,
      label, //optional
      property //optional
    );
  },

  addUserIdToSnowplow(userId) {
    if (!window.__snowplow__ || !userId) {
      return;
    }
    window.__snowplow__('setUserId', userId);
  },

  recordSnowplowPageView(path) {
    try {
      window.__snowplow__('trackPageView:co', path, [
        {
          schema: 'iglu:com.wetransfer/launchdarkly/jsonschema/1-0-0',
          data: {
            userKey: get(window, '__feature_flags__.user.key', ''),
          },
        },
      ]);
    } catch (e) {
      return null;
    }
  },

  recordTrackersPageView(path) {
    const url = new URL(`${location.origin}${path}`);

    if (url.pathname === '/') {
      this.loadTrackingPixel('Wetransfer_site_visit');
    }

    if (url.pathname === '/plus') {
      this.loadTrackingPixel('WeTransfer_Plus_Homepage_Visit');
    }
  },

  pixelQueue: [],
  pixelSDKloaded: false,

  async loadAnalyticSDK() {
    if (!this.allowTrackingPixel()) {
      return;
    }

    await Promise.all([
      scriptLoader(Config.analytic.facebook),
      scriptLoader(Config.analytic.linkedin),
      scriptLoader(Config.analytic.reddit),
      scriptLoader(Config.analytic.teads),
    ]);

    this.pixelSDKloaded = true;

    // Load SDK before resolving the queue
    this.pixelQueue.forEach((script) => script());
    this.pixelQueue = [];
  },

  allowTrackingPixel() {
    return this.hasAccepted() && LaunchDarkly.getVariant(FF_PIXEL_TRACKING);
  },

  loadTrackingPixel(key, params = {}) {
    if (!this.allowTrackingPixel() || !this.pixelSDKloaded) {
      this.pixelQueue.push(() => {
        this.loadTrackingPixel(key, params);
      });

      return;
    }

    // There are 2 ways firing pixel tracker.
    // 1. Load pixel image with tracking param. e.g: <img src=".../px?id=1121947">
    // 2. Call a script provided by SDK. e.g: fbq('track', 'CustomizeProduct');

    const pixels = [];
    const scripts = [];

    if (key in AmazonPixel) {
      pixels.push(AmazonPixel[key]);
    }

    if (key in AppNexusPixel) {
      pixels.push(AppNexusPixel[key]);
    }

    if (key in LinkedinPixel) {
      pixels.push(LinkedinPixel[key]);
    }

    if (key in FacebookScript) {
      scripts.push(FacebookScript[key]);
    }

    if (key in RedditScript) {
      scripts.push(RedditScript[key]);
    }

    scripts.forEach((script) => {
      try {
        script(params);
      } catch (error) {
        TrackJS.track(error);
      }
    });

    pixels.forEach((src) => {
      const pixel = new Image(1, 1);
      pixel.border = 0;
      pixel.alt = '';
      pixel.src = src;
      document.body.appendChild(pixel);
    });
  },
};
